/**
 * WebSocket NFCサービス（Windows用）
 * 既存のwebsocketServiceをINFCServiceインターフェースでラップ
 */

import { INFCService, NFCServiceEvent, NFCEventHandler, NFCCardData } from './nfcServiceInterface';
import { websocketService, WebSocketMessage } from '../websocket';

export class WebSocketNFCService implements INFCService {
  private handlers: Map<string, Set<NFCEventHandler>> = new Map();
  private unsubscribers: (() => void)[] = [];

  // 同一UID連続検出抑制用
  private lastDetectedUid: string | null = null;
  private lastDetectionTime: number = 0;
  private static readonly DEBOUNCE_MS = 5000; // 5秒間同じUIDを無視

  constructor() {}

  async connect(): Promise<void> {
    // WebSocketサービスに接続
    await websocketService.connect();

    // 既存のWebSocketイベントをNFCServiceEventに変換
    this.setupEventBridge();
  }

  private setupEventBridge(): void {
    // 既存のunsubscribersをクリア
    this.unsubscribers.forEach((unsub) => unsub());
    this.unsubscribers = [];

    // connected イベント
    const unsubConnected = websocketService.on('connected', () => {
      this.notifyHandlers({ event: 'connected' });
    });
    this.unsubscribers.push(unsubConnected);

    // disconnected イベント
    const unsubDisconnected = websocketService.on('disconnected', () => {
      this.notifyHandlers({ event: 'disconnected' });
    });
    this.unsubscribers.push(unsubDisconnected);

    // nfc_detected イベント（デバウンス処理付き）
    const unsubNfcDetected = websocketService.on('nfc_detected', (message: WebSocketMessage) => {
      const cardData = message.data as NFCCardData;
      const now = Date.now();

      // 同じUIDが短時間で繰り返し検出された場合は無視
      if (
        cardData.uid === this.lastDetectedUid &&
        now - this.lastDetectionTime < WebSocketNFCService.DEBOUNCE_MS
      ) {
        return;
      }

      // 新しい検出として記録
      this.lastDetectedUid = cardData.uid;
      this.lastDetectionTime = now;

      this.notifyHandlers({
        event: 'nfc_detected',
        data: cardData,
      });
    });
    this.unsubscribers.push(unsubNfcDetected);

    // card_written イベント
    const unsubCardWritten = websocketService.on('card_written', (message: WebSocketMessage) => {
      this.notifyHandlers({
        event: 'card_written',
        data: message.data,
      });
    });
    this.unsubscribers.push(unsubCardWritten);

    // error イベント
    const unsubError = websocketService.on('error', (message: WebSocketMessage) => {
      this.notifyHandlers({
        event: 'error',
        message: message.message,
        context: message.context,
      });
    });
    this.unsubscribers.push(unsubError);
  }

  disconnect(): void {
    // イベントブリッジを解除
    this.unsubscribers.forEach((unsub) => unsub());
    this.unsubscribers = [];

    // WebSocketサービスを切断
    websocketService.disconnect();
  }

  isConnected(): boolean {
    return websocketService.getConnectionState();
  }

  async requestWriteCard(type: number, entityId: number): Promise<void> {
    websocketService.requestWriteCard(type, entityId);
  }

  isWriteMode(): boolean {
    return websocketService.getWriteMode();
  }

  getDeviceId(): string {
    // WebSocketサービスはdevice_idをlocalStorageから取得
    return localStorage.getItem('device_id') || '';
  }

  getServiceType(): 'websocket' | 'web-nfc' | 'mock' {
    return 'websocket';
  }

  // WebSocketサービスでは手動スキャンは不要（常時接続）
  async startManualScan(): Promise<void> {
    // WebSocketは常時接続なので何もしない

    return Promise.resolve();
  }

  on(event: string, handler: NFCEventHandler): () => void {
    if (!this.handlers.has(event)) {
      this.handlers.set(event, new Set());
    }
    this.handlers.get(event)!.add(handler);

    // アンサブスクライブ関数を返す
    return () => {
      this.handlers.get(event)?.delete(handler);
    };
  }

  off(event: string): void {
    this.handlers.delete(event);
  }

  clear(): void {
    this.handlers.clear();
  }

  /**
   * デバウンス状態をクリア（次の検出を許可）
   */
  clearDebounce(): void {
    this.lastDetectedUid = null;
    this.lastDetectionTime = 0;
  }

  private notifyHandlers(event: NFCServiceEvent): void {
    // 特定のイベントハンドラを実行
    const eventHandlers = this.handlers.get(event.event);
    if (eventHandlers) {
      eventHandlers.forEach((handler) => {
        try {
          handler(event);
        } catch (_error) {
          // Event handler error
        }
      });
    }

    // ワイルドカードハンドラを実行
    const wildcardHandlers = this.handlers.get('*');
    if (wildcardHandlers) {
      wildcardHandlers.forEach((handler) => handler(event));
    }
  }
}
